/**
 * Leads Management View
 * Allows Admin and RRHH to manage website contacts, promote them to students, and send bulk emails.
 */
const LeadsView = {
    currentTab: 'all',          // 'all' | 'pending' | 'visit' | 'contacted' | 'rejected' | 'no_consent' | 'config'
    currentSearch: '',
    currentFormationFilter: 'all',
    currentYearFilter: null,    // null (auto) | number (e.g. 2026) | 'all'
    selectedLeads: new Set(),
    // Pagination: 20 by default, the viewer may choose 50/100/200 (remembered on this browser).
    PAGE_SIZES: [20, 50, 100, 200],
    currentPage: 1,
    pageSize() {
        const saved = Number(BrowserState.getItem('leads_page_size'));
        return this.PAGE_SIZES.includes(saved) ? saved : 20;
    },
    editorMode: 'wysiwyg',       // 'wysiwyg' | 'html'
    // Configuración tab: text received from the web forms → SICAF formation.
    interestMappings: null,     // null = not loaded yet
    unassignedTexts: [],
    interestError: null,
    mappingDraft: null,         // {id|null, text, studyPlanId} while a row is being edited
    canManage() { return ['admin', 'coordinator'].includes(AuthManager.currentUser?.role) || AuthManager.hasModule('leads','manage'); },

    // Cálculo del curso lectivo al que aspira un lead:
    // Criterio: de octubre del año X a septiembre del año X+1 -> curso (X+1)/(X+2).
    // Ej: Octubre 2025 hasta Septiembre 2026 -> Curso 2026/2027 (yearStart = 2026).
    todayTargetYearStart() {
        const now = new Date();
        return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
    },

    getLeadTargetYearStart(lead) {
        if (!lead) return this.todayTargetYearStart();
        if (typeof lead.academicYearStart === 'number') return lead.academicYearStart;
        if (typeof lead.academicYear === 'string' && lead.academicYear.includes('/')) {
            const parsed = parseInt(lead.academicYear.split('/')[0], 10);
            if (!isNaN(parsed)) return parsed;
        }
        const d = lead.createdAt ? new Date(lead.createdAt) : new Date();
        if (isNaN(d.getTime())) return this.todayTargetYearStart();
        const month = d.getMonth(); // 0 = Enero, 8 = Septiembre, 9 = Octubre
        const year = d.getFullYear();
        return month >= 9 ? year + 1 : year;
    },

    getLeadTargetYearLabel(lead) {
        const y = typeof lead === 'number' ? lead : this.getLeadTargetYearStart(lead);
        return `${y}/${y + 1}`;
    },

    getAvailableYears(allLeads = []) {
        const years = new Set();
        allLeads.forEach(l => {
            const y = this.getLeadTargetYearStart(l);
            if (typeof y === 'number' && !isNaN(y)) {
                years.add(y);
            }
        });
        // Si no hubiera ningún lead en el sistema, mostrar como mínimo el curso activo
        if (years.size === 0) {
            years.add(this.todayTargetYearStart());
        }
        return [...years].sort((a, b) => b - a);
    },

    setAcademicYear(yearStart) {
        this.currentYearFilter = yearStart === 'all' ? 'all' : parseInt(yearStart, 10);
        this.currentPage = 1;
        this.selectedLeads.clear();
        this.loadLeads();
    },

    // Years that have contacts, as the server reports them (the browser holds one year only).
    yearsList() {
        return Array.isArray(this._years) && this._years.length ? this._years : this.getAvailableYears(LeadService.getLeads());
    },

    /** Loads the contacts of the chosen year (or all, if chosen) and repaints the view. */
    async loadLeads(attempt = 0) {
        const epoch = AuthManager._epoch;
        const year = this.currentYearFilter ?? this.todayTargetYearStart();
        if (this.isFetching) { this._loadAgain = true; return; }
        this.isFetching = true;
        try {
            const result = await LeadService.refreshLeads(year);
            if (epoch !== AuthManager._epoch) return;
            this._years = result.years;
            this._loadedYear = year;
            if (typeof App !== 'undefined' && App.currentView === 'leads') {
                const container = document.getElementById('content-area');
                if (container) {
                    // Repaint without running init again (it would load once more).
                    const originalInit = this.init;
                    this.init = () => { this.selectedLeads.clear(); this.updateBulkEmailButton(); };
                    SafeHTML.set(container, this.render());
                    this.init = originalInit;
                }
            }
        } catch (e) {
            console.error('Error fetching leads:', e);
            Utils.showToast('No se pudieron cargar los contactos.', 'error');
        } finally {
            this.isFetching = false;
        }
        // The view may have moved to another year (the chosen one has no contacts): load that one.
        const moved = this.currentYearFilter !== null && this.currentYearFilter !== undefined && this.currentYearFilter !== this._loadedYear;
        if (this._loadAgain || (moved && attempt < 2)) { this._loadAgain = false; this.loadLeads(attempt + 1); }
    },

    // Study plans (Plan de Estudios → Planes), grouped by formation type. Active plans only;
    // a contact or rule that already has a deactivated plan still shows it.
    studyPlanGroups() {
        if (typeof StudyPlanService === 'undefined') return [];
        return StudyPlanService.CATEGORIES
            .map(category => ({label: category.label, plans: StudyPlanService.byCategory(category.id)}))
            .filter(group => group.plans.length);
    },
    planName(id) {
        return id && typeof StudyPlanService !== 'undefined' ? StudyPlanService.label(id) : null;
    },
    planOptions(selected) {
        const groups = this.studyPlanGroups();
        const listed = groups.some(group => group.plans.some(p => p.id === selected));
        const kept = selected && !listed && this.planName(selected)
            ? `<option value="${Utils.sanitizeHTML(selected)}" selected>${Utils.sanitizeHTML(this.planName(selected))}</option>` : '';
        return kept + groups.map(group => `<optgroup label="${Utils.sanitizeHTML(group.label)}">${group.plans.map(p =>
            `<option value="${Utils.sanitizeHTML(p.id)}" ${selected === p.id ? 'selected' : ''}>${Utils.sanitizeHTML(p.name)}</option>`).join('')}</optgroup>`).join('');
    },

    // Plan filter: the assigned study plan; a contact without one still matches by its text.
    matchesFormation(lead) {
        if (this.currentFormationFilter === 'all') return true;
        if (this.currentFormationFilter === 'unassigned') return !lead.studyPlanId;
        if (lead.studyPlanId) return lead.studyPlanId === this.currentFormationFilter;
        const name = (this.planName(this.currentFormationFilter) || '').toLowerCase();
        return !!name && (lead.interes || '').toLowerCase().includes(name);
    },

    STATUS: {
        pending: ['Sin contestar', 'is-pending'], contacted: ['Contactado', 'is-contacted'], visit: ['Visita', 'is-visit'],
        rejected: ['Descartado', 'is-rejected'], no_consent: ['Sin newsletter', 'is-no-consent'], enrolled: ['Matriculado', 'is-enrolled']
    },
    openLeadId: null,

    // Diseño «T · Contactos»: secciones como botones, cifras del curso, filtros en una barra,
    // lista compacta y el contacto elegido a la derecha (estado con un clic, mensaje, visita y correo).
    render() {
        if (!['admin', 'coordinator', 'rrhh'].includes(AuthManager.currentUser?.role) && !AuthManager.hasModule('leads')) return '<p role="alert">No tienes acceso a los contactos.</p>';
        const allLeads = LeadService.getLeads();
        const availableYears = this.yearsList();

        // Si no hay filtro fijado o el año seleccionado ya no tiene leads, seleccionar el curso activo (si tiene leads) o el primer año disponible
        if (this.currentYearFilter === null || this.currentYearFilter === undefined) {
            this.currentYearFilter = availableYears.includes(this.todayTargetYearStart())
                ? this.todayTargetYearStart()
                : (availableYears[0] || this.todayTargetYearStart());
        } else if (this.currentYearFilter !== 'all' && !availableYears.includes(this.currentYearFilter)) {
            this.currentYearFilter = availableYears.includes(this.todayTargetYearStart())
                ? this.todayTargetYearStart()
                : (availableYears[0] || 'all');
        }

        const leadsInYear = this.currentYearFilter === 'all' ? allLeads
            : allLeads.filter(l => this.getLeadTargetYearStart(l) === parseInt(this.currentYearFilter, 10));
        if (this.currentTab === 'enrolled') this.currentTab = 'all';
        const config = this.currentTab === 'config';
        const unassigned = this.interestMappings === null ? 0 : this.unassignedTexts.length;
        const admin = AuthManager.isAdmin();
        const yearLabel = this.currentYearFilter === 'all' ? 'Todos los cursos' : `Para el curso ${this.currentYearFilter} / ${parseInt(this.currentYearFilter, 10) + 1}`;
        const sec = (on, label, fn, badge) => `<button type="button" class="lv-sec ${on ? 'is-on' : ''}" aria-pressed="${on}" ${UIEvents.attr("click", fn, [])}>${label}${badge ? `<span class="lv-sec-badge">${badge}</span>` : ''}</button>`;
        const mailItem = (type, title, sub) => `<button type="button" role="menuitem" class="lv-mail-item" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;LeadsView.openBulkEmailModal(null,String(captured[0]));}, [type])}><strong>${title}</strong><span>${sub}</span></button>`;

        return `
            <div class="view-container dnr-proposal leads-view lv-page">
                <div class="lv-head">
                    <div class="lv-head-left">
                        <span class="lv-eyebrow">Alumnado y CRM · ${Utils.sanitizeHTML(yearLabel)}</span>
                        <h1 class="lv-title">Contactos</h1>
                    </div>
                    <div class="lv-head-actions">
                        <nav class="lv-secs" aria-label="Secciones">
                            ${sec(!config, `Contactos · ${leadsInYear.length}`, function(event,captured){if(LeadsView.currentTab==='config')LeadsView.switchTab('all');})}
                            ${sec(config, 'Textos de la web', function(event,captured){LeadsView.switchTab('config');}, unassigned)}
                        </nav>
                        ${admin || this.canManage() ? '<span class="lv-divider" aria-hidden="true"></span>' : ''}
                        ${admin ? `<details class="lv-mail">
                            <summary class="lv-btn"><i class="fas fa-envelope" aria-hidden="true"></i><span id="lv-mail-label">Enviar correo</span> <span aria-hidden="true">▾</span></summary>
                            <div class="lv-mail-menu" role="menu">
                                ${mailItem('admissions', 'Correo general', 'A quien acepta newsletter, entre dos fechas, o a los marcados')}
                                ${mailItem('puertas_abiertas', 'Puertas abiertas', 'Invitación a la próxima jornada')}
                                ${mailItem('ultimas_plazas', 'Últimas plazas', 'Aviso a quien aún no se ha inscrito')}
                            </div>
                        </details>` : ''}
                        ${this.canManage() ? `<button type="button" class="lv-btn is-primary" ${UIEvents.attr("click", function(event,captured){LeadsView.openAddModal();}, [])}><i class="fas fa-plus" aria-hidden="true"></i>Añadir contacto</button>` : ''}
                    </div>
                </div>

                ${admin ? '<div id="lv-campaign-status"></div>' : ''}
                <div id="leads-body">${config ? this.renderConfig() : this.renderContacts(leadsInYear, availableYears)}</div>
            </div>
        `;
    },

    renderContacts(leadsInYear, availableYears) {
        let filteredLeads = leadsInYear;
        if (this.currentTab !== 'all') filteredLeads = filteredLeads.filter(l => l.status === this.currentTab);
        // Search filter (name, email, phone, message, interest, origin)
        if (this.currentSearch) {
            const query = this.currentSearch.toLowerCase();
            filteredLeads = filteredLeads.filter(l =>
                (l.nombre || '').toLowerCase().includes(query) ||
                (l.email || '').toLowerCase().includes(query) ||
                (l.telefono || '').toLowerCase().includes(query) ||
                (l.mensaje || '').toLowerCase().includes(query) ||
                (l.interes || '').toLowerCase().includes(query) ||
                (l.origen || '').toLowerCase().includes(query)
            );
        }
        filteredLeads = filteredLeads.filter(l => this.matchesFormation(l));

        const pageSize = this.pageSize();
        const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
        this.currentPage = Math.min(Math.max(1, this.currentPage), totalPages);
        const pageStart = (this.currentPage - 1) * pageSize;
        const pageLeads = filteredLeads.slice(pageStart, pageStart + pageSize);
        // The open contact stays open while it exists, even if a filter hides its row.
        if (!this.openLeadId || !LeadService.getLead(this.openLeadId)) this.openLeadId = pageLeads[0]?.id || null;

        const count = status => leadsInYear.filter(l => l.status === status).length;
        const tabs = [
            { key: 'all', label: 'Todos', count: leadsInYear.length },
            { key: 'pending', label: 'Sin contestar', count: count('pending') },
            { key: 'visit', label: 'Visita', count: count('visit') },
            { key: 'contacted', label: 'Contactados', count: count('contacted') },
            { key: 'rejected', label: 'Descartados', count: count('rejected') },
            { key: 'no_consent', label: 'Sin newsletter', count: count('no_consent') }
        ];
        const admin = AuthManager.isAdmin();

        return `
            ${this.renderStats(leadsInYear)}
            <div class="lv-toolbar">
                <select id="leads-year-select" class="lv-year" ${UIEvents.attr("change", function(event,captured){LeadsView.setAcademicYear(this.value);}, [])} aria-label="Curso de destino">
                    ${availableYears.map(y => `<option value="${y}" ${String(y) === String(this.currentYearFilter) ? 'selected' : ''}>CURSO ${y} / ${y + 1}</option>`).join('')}
                    ${availableYears.length > 1 ? `<option value="all" ${this.currentYearFilter === 'all' ? 'selected' : ''}>TODOS LOS CURSOS</option>` : ''}
                </select>
                <div class="lv-tabs" role="group" aria-label="Estado">
                    ${tabs.map(t => `<button type="button" class="lv-tab ${this.currentTab === t.key ? 'is-on' : ''}" aria-pressed="${this.currentTab === t.key}" ${UIEvents.attr("click", function(event,captured){LeadsView.switchTab(String(captured[0]));}, [t.key])}>${Utils.sanitizeHTML(t.label)} · ${t.count}</button>`).join('')}
                </div>
                <label class="lv-formation-filter">
                    <span class="dv-sr-only">Plan de estudios</span>
                    <select ${UIEvents.attr("change", function(event,captured){LeadsView.handleFormationFilter(this.value);}, [])}>
                        <option value="all" ${this.currentFormationFilter === 'all' ? 'selected' : ''}>Todos los planes</option>
                        <option value="unassigned" ${this.currentFormationFilter === 'unassigned' ? 'selected' : ''}>Sin plan asignado</option>
                        ${this.planOptions(this.currentFormationFilter)}
                    </select>
                </label>
                <label class="lv-search">
                    <i class="fas fa-search" aria-hidden="true"></i>
                    <input type="search" id="leads-search-input" placeholder="Nombre, correo, teléfono u origen" aria-label="Buscar contactos" ${UIEvents.attr("input", function(event,captured){LeadsView.handleSearch(this.value);}, [])} value="${Utils.sanitizeHTML(this.currentSearch)}">
                </label>
            </div>

            <div class="lv-layout">
                <section class="lv-card" id="leads-list" aria-labelledby="lv-list-title">
                    <div class="lv-list-head" id="lv-list-head">
                        <h2 class="lv-h2" id="lv-list-title">Contactos · ${filteredLeads.length}</h2>
                        <span class="lv-muted">${admin ? 'Pulsa uno para verlo · márcalos para escribirles juntos' : 'Pulsa uno para verlo'}</span>
                    </div>
                    ${admin ? `<div class="lv-bulk" id="lv-bulk" role="region" aria-label="Acciones con los marcados" hidden>
                        <strong id="lv-bulk-count"></strong>
                        <span class="lv-bulk-actions">
                            <button type="button" ${UIEvents.attr("click", function(event,captured){LeadsView.openBulkEmailModal(null,'puertas_abiertas');}, [])}>Puertas abiertas</button>
                            <button type="button" ${UIEvents.attr("click", function(event,captured){LeadsView.openBulkEmailModal(null,'ultimas_plazas');}, [])}>Últimas plazas</button>
                            <button type="button" class="is-main" ${UIEvents.attr("click", function(event,captured){LeadsView.openBulkEmailModal(null,'admissions');}, [])}>Escribirles</button>
                            <button type="button" class="is-clear" aria-label="Quitar la selección" ${UIEvents.attr("click", function(event,captured){LeadsView.toggleSelectAll(false);const all=document.getElementById('select-all-leads');if(all)all.checked=false;}, [])}>✕</button>
                        </span>
                    </div>` : ''}
                    <div class="lv-scroll">
                        <table class="lv-table">
                            <thead><tr>
                                ${admin ? `<th scope="col" class="lv-col-check"><input type="checkbox" id="select-all-leads" aria-label="Marcar todos los de esta página" ${UIEvents.attr("change", function(event,captured){LeadsView.toggleSelectAll(this.checked);}, [])}></th>` : ''}
                                <th scope="col">Contacto</th>
                                <th scope="col" class="lv-col-plan">Le interesa</th>
                                <th scope="col" class="lv-col-when">Llegó</th>
                                <th scope="col" class="lv-col-state">Estado</th>
                            </tr></thead>
                            <tbody id="leads-tbody">
                                ${pageLeads.length ? pageLeads.map(l => this.renderRow(l)).join('') : `<tr><td colspan="${admin ? 5 : 4}"><div class="dnr-empty"><strong>No se encontraron contactos</strong><p>Prueba a cambiar los filtros o a añadir un contacto.</p></div></td></tr>`}
                            </tbody>
                        </table>
                    </div>
                    ${this.renderPager(filteredLeads.length, pageStart, pageLeads.length, totalPages, pageSize)}
                </section>
                <aside class="lv-card lv-detail" id="lv-detail" aria-live="polite">${this.renderDetail(LeadService.getLead(this.openLeadId))}</aside>
            </div>`;
    },

    renderStats(leads) {
        const now = Date.now(), day = 86400000;
        const week = leads.filter(l => now - new Date(l.createdAt).getTime() < 7 * day).length;
        const withNews = leads.filter(l => l.newsletter).length;
        const pending = leads.filter(l => l.status === 'pending');
        const oldest = pending.reduce((min, l) => Math.min(min, new Date(l.createdAt).getTime() || Infinity), Infinity);
        const waited = Number.isFinite(oldest) ? Math.floor((now - oldest) / day) : null;
        const visitors = leads.filter(l => this.wantsVisit(l));
        const toInvite = visitors.filter(l => !l.visitInvitedAt && l.status !== 'enrolled' && !this.isStudent(l)).length;
        const joined = leads.filter(l => l.matches && (l.matches.students?.length || l.matches.applications?.length)).length;
        const pct = leads.length ? Math.round(joined / leads.length * 100) : 0;
        return `
            <div class="lv-stats" id="lv-stats">
                <div class="lv-stat"><span class="lv-stat-label">Contactos de este curso</span><span class="lv-stat-value">${leads.length}</span><span class="lv-stat-sub">${week} ${week === 1 ? 'nuevo' : 'nuevos'} esta semana · ${withNews} con newsletter</span></div>
                <div class="lv-stat ${pending.length ? 'is-alert' : ''}"><span class="lv-stat-label">Sin contestar</span><span class="lv-stat-value">${pending.length}</span><span class="lv-stat-sub">${waited === null ? 'Todos contestados' : waited === 0 ? 'El más antiguo llegó hoy' : `El más antiguo llegó hace ${waited} ${waited === 1 ? 'día' : 'días'}`}</span></div>
                <div class="lv-stat"><span class="lv-stat-label">Quieren visitar</span><span class="lv-stat-value">${visitors.length}</span><span class="lv-stat-sub">${toInvite ? `${toInvite} sin invitación enviada` : 'Todos invitados'}</span></div>
                <div class="lv-stat"><span class="lv-stat-label">Ya se han inscrito</span><span class="lv-stat-value">${joined} <small>· ${pct} %</small></span><span class="lv-stat-sub">Mismo correo en una solicitud o alumno</span></div>
            </div>`;
    },

    arrived(lead) {
        const d = new Date(lead.createdAt);
        if (isNaN(d)) return '—';
        const today = new Date(), yesterday = new Date(today.getTime() - 86400000);
        const same = (a, b) => a.toDateString() === b.toDateString();
        if (same(d, today)) return 'hoy ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        if (same(d, yesterday)) return 'ayer';
        return d.toLocaleDateString('es-ES', d.getFullYear() === today.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
    },

    renderRow(lead) {
        const isChecked = this.selectedLeads.has(lead.id);
        const open = lead.id === this.openLeadId;
        const [label, modifier] = this.STATUS[lead.status] || ['Desconocido', 'is-pending'];
        // Visit request from the web form; the exact answer is the tooltip. Once the one-off
        // invitation has been sent, the chip says when.
        const visitAnswer = String(lead.visita || '').trim();
        const invitedOn = lead.visitInvitedAt ? new Date(lead.visitInvitedAt).toLocaleDateString('es-ES') : '';
        const visitChip = !this.wantsVisit(lead) ? ''
            : invitedOn ? `<span class="lv-chip is-done" title="${Utils.sanitizeHTML(visitAnswer)} · invitación enviada el ${invitedOn}">Invitado</span>`
            : `<span class="lv-chip is-visit" title="${Utils.sanitizeHTML(visitAnswer)}">Quiere visitar</span>`;
        return `
            <tr class="${isChecked ? 'is-selected' : ''} ${open ? 'is-open' : ''}" data-lead="${Utils.sanitizeHTML(lead.id)}" ${UIEvents.attr("click", function(event,captured){if(!event.target.closest('input,a,button'))LeadsView.selectLead(String(captured[0]));}, [lead.id])}>
                ${AuthManager.isAdmin() ? `<td class="lv-col-check"><input type="checkbox" class="lead-checkbox" data-id="${Utils.sanitizeHTML(lead.id)}" aria-label="Marcar a ${Utils.sanitizeHTML(lead.nombre)}" ${isChecked ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){LeadsView.toggleSelectLead(String(captured[0]),this.checked);}, [lead.id])}></td>` : ''}
                <td>
                    <span class="lv-who">
                        <span class="lv-who-line"><button type="button" class="lv-name-link" ${UIEvents.attr("click", function(event,captured){LeadsView.selectLead(String(captured[0]));}, [lead.id])}>${Utils.sanitizeHTML(lead.nombre)}</button>${visitChip}${lead.newsletter ? '' : '<span class="lv-chip" title="No quiere recibir correos">Sin newsletter</span>'}</span>
                        <span class="lv-muted">${Utils.sanitizeHTML([lead.email, lead.telefono].filter(Boolean).join(' · '))}</span>
                    </span>
                </td>
                <td class="lv-col-plan">${this.renderFormationCell(lead)}</td>
                <td class="lv-col-when"><span class="lv-when">${Utils.sanitizeHTML(this.arrived(lead))}</span>${lead.origen ? `<span class="lv-muted">${Utils.sanitizeHTML(lead.origen)}</span>` : ''}</td>
                <td class="lv-col-state"><span class="lv-status-badge ${modifier}">${label}</span>${this.renderMatchChip(lead)}</td>
            </tr>
        `;
    },

    renderDetail(lead) {
        if (!lead) return '<p class="lv-muted">Elige un contacto de la lista para verlo aquí.</p>';
        const manage = this.canManage(), admin = AuthManager.isAdmin();
        const plan = this.planName(lead.studyPlanId) || lead.interes || 'Sin especificar';
        const fact = (label, value) => `<div><dt>${label}</dt><dd>${value}</dd></div>`;
        const statusButtons = ['pending', 'contacted', 'visit', 'rejected', 'no_consent'].map(id => {
            const on = lead.status === id;
            return `<button type="button" role="radio" aria-checked="${on}" class="lv-state ${on ? 'is-on' : ''}" ${manage ? UIEvents.attr("click", function(event,captured){LeadsView.setStatus(String(captured[0]),String(captured[1]));}, [lead.id, id]) : 'disabled'}>${this.STATUS[id][0]}</button>`;
        }).join('');
        const visitAnswer = String(lead.visita || '').trim();
        const summary = this.matchSummary(lead);
        return `
            <div class="lv-detail-head">
                <div>
                    <span class="lv-eyebrow">Llegó ${Utils.sanitizeHTML(this.arrived(lead))}${lead.origen ? ' · ' + Utils.sanitizeHTML(lead.origen) : ''}</span>
                    <h2 class="lv-detail-name">${Utils.sanitizeHTML(lead.nombre)}</h2>
                </div>
                <span class="lv-detail-tools">
                    ${manage ? `<button type="button" class="lv-icon-btn" title="Editar" aria-label="Editar contacto" ${UIEvents.attr("click", function(event,captured){LeadsView.openEditModal(String(captured[0]));}, [lead.id])}><i class="fas fa-pen" aria-hidden="true"></i></button>` : ''}
                    ${admin ? `<button type="button" class="lv-icon-btn is-danger" title="Eliminar" aria-label="Eliminar contacto" ${UIEvents.attr("click", function(event,captured){LeadsView.deleteLead(String(captured[0]));}, [lead.id])}><i class="fas fa-trash" aria-hidden="true"></i></button>` : ''}
                </span>
            </div>
            <div class="lv-field">
                <span class="lv-label">Estado</span>
                <div class="lv-states" role="radiogroup" aria-label="Estado del contacto">${statusButtons}</div>
                ${lead.status === 'enrolled' ? '<span class="lv-muted">Marcado como matriculado.</span>' : ''}
            </div>
            <dl class="lv-facts">
                ${fact('Correo', `<a href="mailto:${Utils.sanitizeHTML(lead.email)}">${Utils.sanitizeHTML(lead.email)}</a>`)}
                ${fact('Teléfono', lead.telefono ? `<a href="tel:${Utils.sanitizeHTML(lead.telefono)}">${Utils.sanitizeHTML(lead.telefono)}</a>` : '—')}
                ${fact('Le interesa', Utils.sanitizeHTML(plan) + (!lead.studyPlanId && lead.interes ? ' <span class="lv-unassigned-chip">Sin asignar</span>' : ''))}
                ${fact('Curso', Utils.sanitizeHTML(this.getLeadTargetYearLabel(lead)))}
                ${lead.lugar ? fact('Lugar', Utils.sanitizeHTML(lead.lugar)) : ''}
                ${fact('Newsletter', lead.newsletter ? 'Sí, acepta correos' : 'No quiere correos')}
            </dl>
            ${lead.mensaje ? `<div class="lv-field"><span class="lv-label">Su mensaje</span><p class="lv-message">${Utils.sanitizeHTML(lead.mensaje)}</p></div>` : ''}
            ${this.wantsVisit(lead) ? `<div class="lv-visit">
                <span><strong>Quiere visitar la escuela</strong><span>${Utils.sanitizeHTML(visitAnswer)}${lead.visitInvitedAt ? ` · invitación enviada el ${new Date(lead.visitInvitedAt).toLocaleDateString('es-ES')}` : ''}</span></span>
                ${this.canInviteToVisit(lead) ? `<button type="button" class="lv-btn is-primary is-small" title="Invitar a visitar la escuela (un solo correo)" ${UIEvents.attr("click", function(event,captured){LeadsView.openBulkEmailModal(String(captured[0]),'invitacion_visita');}, [lead.id])}><i class="fas fa-calendar-check" aria-hidden="true"></i>Invitar</button>` : ''}
            </div>` : ''}
            ${summary ? `<div class="lv-match"><strong>Con este correo: ${Utils.sanitizeHTML(summary.label)}</strong><span>Solo informativo: el estado lo decides tú.${this.isStudent(lead) ? ' Al ser alumno, no recibe campañas ni invitaciones.' : ''}</span></div>` : ''}
            ${AuthManager.isAdmin() && lead.status !== 'enrolled' && lead.newsletter && !this.isStudent(lead) ? `<button type="button" class="lv-btn lv-detail-mail" title="Enviar Correo" ${UIEvents.attr("click", function(event,captured){LeadsView.openBulkEmailModal(String(captured[0]));}, [lead.id])}><i class="fas fa-paper-plane" aria-hidden="true"></i>Escribirle un correo</button>` : ''}`;
    },

    selectLead(id) {
        this.openLeadId = id;
        document.querySelectorAll('.leads-view #leads-tbody tr[data-lead]').forEach(tr => tr.classList.toggle('is-open', tr.dataset.lead === id));
        const box = document.getElementById('lv-detail');
        if (box) SafeHTML.set(box, this.renderDetail(LeadService.getLead(id)));
    },

    // Estado con un clic desde el panel: solo cambia el estado; el servidor guarda el resto igual.
    async setStatus(id, status) {
        if (!this.canManage() || this._saving) return;
        const lead = LeadService.getLead(id);
        if (!lead || lead.status === status) return;
        this._saving = true;
        try {
            await LeadService.updateLead(id, { status });
            Utils.showToast(`${lead.nombre}: ${this.STATUS[status][0].toLowerCase()}`, 'success');
            this.patch('#leads-body');
        } catch (error) {
            Utils.showToast(error.message || 'No se pudo cambiar el estado.', 'error');
        } finally { this._saving = false; }
    },

    renderPager(total, start, shown, totalPages, pageSize) {
        if (!total) return '';
        const page = this.currentPage;
        // Page numbers around the current one, with the first and last always reachable.
        const pages = [...new Set([1, page - 1, page, page + 1, totalPages])].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
        let previous = 0;
        const numbers = pages.map(p => {
            const gap = p - previous > 1 ? '<span class="sv-pagination-info" aria-hidden="true">…</span>' : '';
            previous = p;
            return gap + `<button type="button" class="sv-page-btn ${p === page ? 'active' : ''}" ${p === page ? 'aria-current="page"' : ''} ${UIEvents.attr("click", function(event,captured){LeadsView.changePage(captured[0]);}, [p])}>${p}</button>`;
        }).join('');
        return `
            <div class="sv-pagination lv-pagination">
                <div class="sv-pagination-info">Mostrando <strong>${start + 1}-${start + shown}</strong> de <strong>${total}</strong> contactos</div>
                <div class="lv-pagination-controls">
                    <label class="lv-page-size">
                        <span class="sv-pagination-info">Por página:</span>
                        <select class="sv-rows-select" ${UIEvents.attr("change", function(event,captured){LeadsView.changePageSize(this.value);}, [])}>
                            ${this.PAGE_SIZES.map(size => `<option value="${size}" ${size === pageSize ? 'selected' : ''}>${size}</option>`).join('')}
                        </select>
                    </label>
                    ${totalPages > 1 ? `<div class="lv-page-buttons">
                        <button type="button" class="sv-page-btn" aria-label="Página anterior" ${page === 1 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){LeadsView.changePage(captured[0]);}, [page - 1])}><i class="fas fa-chevron-left" style="font-size: 0.8rem;"></i></button>
                        ${numbers}
                        <button type="button" class="sv-page-btn" aria-label="Página siguiente" ${page === totalPages ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){LeadsView.changePage(captured[0]);}, [page + 1])}><i class="fas fa-chevron-right" style="font-size: 0.8rem;"></i></button>
                    </div>` : ''}
                </div>
            </div>`;
    },

    changePage(page) {
        this.currentPage = Number(page) || 1;
        this.selectedLeads.clear();
        this.patch('#leads-list');
        document.querySelector('.leads-view #leads-list')?.scrollIntoView?.({block: 'nearest'});
    },

    changePageSize(size) {
        if (!this.PAGE_SIZES.includes(Number(size))) return;
        BrowserState.setItem('leads_page_size', String(size));
        this.currentPage = 1;
        this.selectedLeads.clear();
        this.patch('#leads-list');
    },

    // Repaints only part of the view (tabs + list, or just the rows) without moving the page:
    // a full repaint collapsed the page for an instant and the scroll jumped to the top.
    patch(selector, fallback = true) {
        const current = document.querySelector('.leads-view ' + selector);
        if (!current || typeof App === 'undefined' || App.currentView !== 'leads') return fallback ? this.rerender(false) : false;
        const scrollers = [document.scrollingElement, document.querySelector('.main-content'), document.getElementById('content-area')]
            .filter(Boolean).map(element => [element, element.scrollTop]);
        const draft = document.createElement('div');
        SafeHTML.set(draft, this.render());
        const fresh = draft.querySelector(selector);
        if (!fresh) return fallback ? this.rerender(false) : false;
        current.replaceWith(fresh);
        for (const [element, top] of scrollers) element.scrollTop = top;
        this.updateBulkEmailButton();
        return true;
    },

    // What this e-mail became (server: lead-matches.js). Information only: it never changes the
    // contact's status, because anyone can type someone else's e-mail in Inscríbete.
    APPLICATION_STATUS: { pending: 'solicitud enviada', interview: 'en entrevista', payment: 'en pago' },
    isStudent(lead) {
        return !!lead?.matches?.students?.length;
    },
    matchSummary(lead) {
        const m = lead.matches;
        if (!m) return null;
        if (m.students.length) {
            const formations = [...new Set(m.students.flatMap(s => s.formations))];
            return { kind: 'student', label: 'Alumno' + (formations.length ? ': ' + formations.join(', ') : '') };
        }
        if (m.applications.length) {
            const items = m.applications.map(a => `${a.formation} (${this.APPLICATION_STATUS[a.status] || a.status})`);
            return { kind: 'application', label: 'Inscrito: ' + items.join(', ') };
        }
        return null;
    },
    renderMatchChip(lead) {
        const summary = this.matchSummary(lead);
        if (!summary) return '';
        const icon = summary.kind === 'student' ? 'fa-user-graduate' : 'fa-file-signature';
        return `<div><span class="lv-match-chip is-${summary.kind}" title="${Utils.sanitizeHTML(summary.label + ' — coincide por email; no cambia el estado del contacto')}"><i class="fas ${icon}" aria-hidden="true"></i> ${Utils.sanitizeHTML(summary.label)}</span></div>`;
    },
    // Assigned study plan, or the text received with a "sin asignar" mark.
    renderFormationCell(lead) {
        const name = this.planName(lead.studyPlanId);
        if (name) {
            return `<span class="lv-formation-name" title="${Utils.sanitizeHTML(lead.interes ? 'Texto recibido: ' + lead.interes : '')}">${Utils.sanitizeHTML(name)}</span>`;
        }
        if (!lead.interes) return '<span class="lv-empty-cell" style="font-style: italic;">Sin especificar</span>';
        return `<span class="lv-formation-raw">${Utils.sanitizeHTML(lead.interes)}</span> <span class="lv-unassigned-chip" title="Asigna este texto a una formación en la pestaña Configuración">Sin asignar</span>`;
    },

    // --- Configuración: text received from the web forms → study plan ---
    renderConfig() {
        const manage = this.canManage();
        const hasPlans = this.studyPlanGroups().length > 0;
        const draft = this.mappingDraft;
        const draftRow = `
            <form class="lv-map-form" ${UIEvents.attr("submit", function(event,captured){LeadsView.saveMapping(event);}, [])}>
                <label class="lv-map-field">
                    <span>Texto que llega de la web</span>
                    <input type="text" name="text" class="form-input" maxlength="300" required value="${Utils.sanitizeHTML(draft?.text || '')}" placeholder="Texto tal como llega del formulario web">
                </label>
                <label class="lv-map-field">
                    <span>Plan de estudios</span>
                    <select name="studyPlanId" class="form-input" required>
                        <option value="">Elige plan de estudios…</option>
                        ${this.planOptions(draft?.studyPlanId)}
                    </select>
                </label>
                <div class="lv-map-actions">
                    <button type="submit" class="lv-btn is-primary is-small"><i class="fas fa-check" aria-hidden="true"></i>Guardar</button>
                    <button type="button" class="lv-btn is-small" ${UIEvents.attr("click", function(event,captured){LeadsView.cancelMapping();}, [])}>Cancelar</button>
                </div>
            </form>`;

        let mappingsBody;
        if (this.interestError) {
            mappingsBody = `<div class="lv-note is-error" role="alert"><i class="fas fa-triangle-exclamation"></i> ${Utils.sanitizeHTML(this.interestError)}
                <button type="button" class="lv-btn is-small" ${UIEvents.attr("click", function(event,captured){LeadsView.loadInterestMappings(true);}, [])}>Reintentar</button></div>`;
        } else if (this.interestMappings === null) {
            mappingsBody = '<p class="lv-note"><i class="fas fa-spinner fa-spin"></i> Cargando reglas…</p>';
        } else {
            const rows = this.interestMappings.map(m => draft && draft.id === m.id ? `<li class="lv-map-row is-editing">${draftRow}</li>` : `
                <li class="lv-map-row">
                    <span class="lv-map-text">${Utils.sanitizeHTML(m.text)}</span>
                    <i class="fas fa-arrow-right lv-map-arrow" aria-hidden="true"></i>
                    <span class="lv-map-formation">${this.planName(m.studyPlanId) ? Utils.sanitizeHTML(this.planName(m.studyPlanId)) : '<em>Plan no disponible</em>'}</span>
                    ${manage ? `<span class="lv-map-row-actions">
                        <button type="button" class="lv-icon-btn" title="Editar" aria-label="Editar regla" ${UIEvents.attr("click", function(event,captured){LeadsView.editMapping(String(captured[0]));}, [m.id])}><i class="fas fa-pen"></i></button>
                        <button type="button" class="lv-icon-btn is-danger" title="Eliminar" aria-label="Eliminar regla" ${UIEvents.attr("click", function(event,captured){LeadsView.deleteMapping(String(captured[0]));}, [m.id])}><i class="fas fa-trash"></i></button>
                    </span>` : ''}
                </li>`).join('');
            mappingsBody = `
                ${draft && !draft.id ? `<div class="lv-map-new">${draftRow}</div>` : ''}
                ${this.interestMappings.length ? `<ul class="lv-map-list">${rows}</ul>`
                    : (draft ? '' : '<p class="lv-note">Todavía no hay reglas. Pulsa «Nueva regla» o «Asignar» en un texto sin asignar.</p>')}`;
        }

        const pending = this.interestMappings === null ? [] : this.unassignedTexts;
        const unassigned = this.interestMappings === null ? '<p class="lv-note"><i class="fas fa-spinner fa-spin"></i> Cargando…</p>' : pending.length ? `
            <ul class="lv-map-list">
                ${pending.map(u => `
                    <li class="lv-map-row">
                        <span class="lv-map-text">${Utils.sanitizeHTML(u.text)}<span class="lv-map-count">${u.count} ${u.count === 1 ? 'contacto' : 'contactos'}</span></span>
                        ${manage && hasPlans ? `<button type="button" class="lv-btn is-small" ${UIEvents.attr("click", function(event,captured){LeadsView.newMapping(String(captured[0]));}, [u.text])}><i class="fas fa-link" aria-hidden="true"></i>Asignar</button>` : ''}
                    </li>`).join('')}
            </ul>` : '<p class="lv-note"><i class="fas fa-check-circle" style="color: var(--dnr-success);"></i> Todos los textos recibidos tienen plan de estudios.</p>';

        return `
            <div class="lv-config">
                <section class="lv-card lv-config-card" aria-labelledby="lv-rules-title">
                    <div class="lv-config-head">
                        <div>
                            <h2 class="lv-h2" id="lv-rules-title">Texto de la web → plan de estudios${this.interestMappings ? ` · ${this.interestMappings.length}` : ''}</h2>
                            <p class="lv-note">Cuando llega un contacto, SICAF busca su texto aquí y le pone el plan (por ejemplo, Dirección Cinematográfica agrupa 1.º, 2.º y 3.º). No distingue mayúsculas ni espacios de más, y al guardar también se aplica a los contactos que ya tenían ese texto.</p>
                        </div>
                        ${manage ? `<button type="button" class="lv-btn is-primary is-small" ${hasPlans ? '' : 'disabled'} ${UIEvents.attr("click", function(event,captured){LeadsView.newMapping('');}, [])}><i class="fas fa-plus" aria-hidden="true"></i>Nueva regla</button>` : ''}
                    </div>
                    ${mappingsBody}
                </section>
                <section class="lv-card lv-config-card ${pending.length ? 'is-alert' : ''}" aria-labelledby="lv-pending-title">
                    <div class="lv-config-head">
                        <div>
                            <h2 class="lv-h2" id="lv-pending-title">Sin asignar${pending.length ? ` · ${pending.length}` : ''}</h2>
                            <p class="lv-note">Textos que han llegado de la web y aún no tienen plan. Sus contactos salen como «Sin asignar» en la lista.</p>
                        </div>
                    </div>
                    ${unassigned}
                </section>
            </div>`;
    },

    rerender(partial = true) {
        const container = document.getElementById('content-area');
        if (!container || typeof App === 'undefined' || App.currentView !== 'leads') return;
        if (partial && document.getElementById('leads-body') && this.patch('#leads-body', false)) return;
        SafeHTML.set(container, this.render());
        this.updateBulkEmailButton();
    },

    async loadInterestMappings(force = false) {
        if (this._loadingMappings || (this.interestMappings !== null && !force)) return;
        this._loadingMappings = true;
        const epoch = AuthManager._epoch;
        this.interestError = null;
        try {
            const result = await LeadService.getInterestMappings();
            if (epoch !== AuthManager._epoch) return;
            this.interestMappings = result.mappings || [];
            this.unassignedTexts = result.unassigned || [];
        } catch (error) {
            if (epoch !== AuthManager._epoch) return;
            this.interestError = error.message || 'No se pudieron cargar las asignaciones.';
        } finally {
            this._loadingMappings = false;
        }
        if (this.currentTab === 'config') this.rerender();
        else this.patch('.lv-secs', false); // «Textos de la web» shows how many are unassigned.
    },

    newMapping(text) {
        if (!this.canManage()) return;
        this.mappingDraft = {id: null, text: text || '', studyPlanId: ''};
        this.rerender();
        document.querySelector(text ? '.lv-map-form select[name="studyPlanId"]' : '.lv-map-form input[name="text"]')?.focus();
    },

    editMapping(id) {
        const mapping = (this.interestMappings || []).find(m => m.id === id);
        if (!mapping || !this.canManage()) return;
        this.mappingDraft = {id, text: mapping.text, studyPlanId: mapping.studyPlanId};
        this.rerender();
        document.querySelector('.lv-map-form input[name="text"]')?.focus();
    },

    cancelMapping() {
        this.mappingDraft = null;
        this.rerender();
    },

    async saveMapping(event) {
        event.preventDefault();
        if (!this.canManage() || this._savingMapping || !this.mappingDraft) return;
        const form = event.target;
        if (!form.checkValidity()) { form.reportValidity(); return; }
        const data = {text: form.elements.text.value, studyPlanId: form.elements.studyPlanId.value};
        // Keep what was typed if saving fails.
        this.mappingDraft = {...this.mappingDraft, ...data};
        this._savingMapping = true;
        const epoch = AuthManager._epoch;
        form.querySelectorAll('button').forEach(button => { button.disabled = true; });
        try {
            const result = await LeadService.saveInterestMapping(this.mappingDraft.id, data);
            if (epoch !== AuthManager._epoch) return;
            this.mappingDraft = null;
            await this.loadLeads();
            await this.loadInterestMappings(true);
            Utils.showToast(result.applied ? `Asignación guardada. ${result.applied} ${result.applied === 1 ? 'contacto actualizado' : 'contactos actualizados'}.` : 'Asignación guardada.', 'success');
        } catch (error) {
            if (epoch === AuthManager._epoch) {
                Utils.showToast(error.message, 'error');
                this.rerender();
            }
        } finally {
            this._savingMapping = false;
        }
    },

    async deleteMapping(id) {
        const mapping = (this.interestMappings || []).find(m => m.id === id);
        if (!mapping || !this.canManage() || this._savingMapping) return;
        const answer = await SafeHTML.fire({
            icon: 'warning', title: '¿Eliminar asignación?',
            text: `Los contactos con el texto «${mapping.text}» que la recibieron de forma automática quedarán sin plan de estudios. Los elegidos a mano no cambian.`,
            showCancelButton: true, confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar'
        });
        if (!answer?.isConfirmed) return;
        this._savingMapping = true;
        const epoch = AuthManager._epoch;
        try {
            await LeadService.deleteInterestMapping(id);
            if (epoch !== AuthManager._epoch) return;
            await this.loadLeads();
            await this.loadInterestMappings(true);
            Utils.showToast('Asignación eliminada.', 'success');
        } catch (error) {
            if (epoch === AuthManager._epoch) Utils.showToast(error.message, 'error');
        } finally {
            this._savingMapping = false;
        }
    },

    async init() {
        if (!['admin', 'coordinator', 'rrhh'].includes(AuthManager.currentUser?.role) && !AuthManager.hasModule('leads')) return;
        const epoch = AuthManager._epoch;
        this.selectedLeads.clear();
        this.updateBulkEmailButton();
        if (this.currentTab === 'config' || this.canManage()) this.loadInterestMappings(true);
        this.refreshCampaigns();

        // Contacts of the chosen year, fresh from the server.
        if (epoch === AuthManager._epoch) await this.loadLeads();
    },

    // --- Search & Filters ---
    handleSearch(val) {
        this.currentSearch = val;
        // Waits until typing pauses; then only the list (rows and pager) is repainted, so the
        // search box keeps its focus and the page does not move.
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
            this.currentPage = 1;
            this.selectedLeads.clear();
            this.patch('#leads-list');
        }, 250);
    },

    handleFormationFilter(val) {
        this.currentFormationFilter = val;
        this.currentPage = 1;
        this.patch('#leads-body');
    },

    switchTab(tab) {
        this.currentTab = tab;
        this.currentPage = 1;
        this.mappingDraft = null;
        this.selectedLeads.clear(); // Clear selections when switching tabs
        if (tab === 'config') this.loadInterestMappings(true);
        this.patch('#leads-body');
        this.patch('.lv-secs', false);
    },

    // --- Checkbox Selection ---
    toggleSelectLead(id, checked) {
        if (checked) {
            this.selectedLeads.add(id);
        } else {
            this.selectedLeads.delete(id);
        }
        this.updateBulkEmailButton();
        
        // Update row highlight (clase .is-selected, ver leads.css)
        const row = document.querySelector(`input[data-id="${id}"]`)?.closest('tr');
        if (row) {
            row.classList.toggle('is-selected', checked);
        }

        // Synchronize "Select All" checkbox status
        const checkboxes = document.querySelectorAll('.lead-checkbox');
        const selectAllCheckbox = document.getElementById('select-all-leads');
        if (selectAllCheckbox) {
            const allChecked = Array.from(checkboxes).every(cb => cb.checked);
            selectAllCheckbox.checked = allChecked && checkboxes.length > 0;
        }
    },

    toggleSelectAll(checked) {
        const checkboxes = document.querySelectorAll('.lead-checkbox');
        checkboxes.forEach(cb => {
            const id = cb.dataset.id;
            cb.checked = checked;
            if (checked) {
                this.selectedLeads.add(id);
            } else {
                this.selectedLeads.delete(id);
            }
            const row = cb.closest('tr');
            if (row) {
                row.classList.toggle('is-selected', checked);
            }
        });
        this.updateBulkEmailButton();
    },

    // Marked contacts: the mail menu says how many, and the list header becomes the dark bar.
    updateBulkEmailButton() {
        const count = this.selectedLeads.size;
        const label = document.getElementById('lv-mail-label');
        if (label) label.textContent = count ? `Escribir a ${count}` : 'Enviar correo';
        const head = document.getElementById('lv-list-head'), bulk = document.getElementById('lv-bulk');
        if (head && bulk) { head.hidden = count > 0; bulk.hidden = count === 0; }
        const text = document.getElementById('lv-bulk-count');
        if (text) text.textContent = `${count} ${count === 1 ? 'contacto marcado' : 'contactos marcados'}`;
    },

    // --- Actions ---

    openAddModal() {
        if (!this.canManage()) return;
        const formations = FormationService.getFormations();
        const currentYear = this.todayTargetYearStart();
        const creationYears = new Set([currentYear, currentYear + 1, ...this.yearsList()]);
        const availableYears = [...creationYears].sort((a, b) => b - a);
        const defaultYear = (this.currentYearFilter && this.currentYearFilter !== 'all')
            ? parseInt(this.currentYearFilter, 10)
            : currentYear;

        const content = `
            <div class="dnr-proposal leads-form-dnr">
            <form id="add-lead-form" ${UIEvents.attr("submit", function(event,captured){LeadsView.handleAddLead(event);}, [])}>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Nombre y Apellidos *</label>
                        <input type="text" name="nombre" class="form-input" required placeholder="Nombre y apellidos">
                    </div>
                    <div class="form-group">
                        <label class="form-label">Email *</label>
                        <input type="email" name="email" class="form-input" required placeholder="Correo electrónico">
                    </div>
                </div>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Teléfono</label>
                        <input type="tel" name="telefono" class="form-input" placeholder="Teléfono">
                    </div>
                    <div class="form-group">
                        <label class="form-label">Plan de estudios de interés</label>
                        <select name="studyPlanId" class="form-input" style="cursor: pointer;">
                            <option value="">Selecciona plan de estudios...</option>
                            ${this.planOptions('')}
                        </select>
                    </div>
                </div>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Curso Lectivo de Destino *</label>
                        <select name="academicYearStart" class="form-input" style="cursor: pointer;" required>
                            ${availableYears.map(y => `<option value="${y}" ${y === defaultYear ? 'selected' : ''}>Curso ${y} / ${y + 1}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Origen</label>
                        <input type="text" name="origen" class="form-input" value="manual" placeholder="Ej: web, feria, recomendación">
                    </div>
                </div>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Lugar/Sede</label>
                        <input type="text" name="lugar" class="form-input" placeholder="Municipio">
                    </div>
                </div>
                <div class="form-group">
                    <label class="form-label">Mensaje o Notas</label>
                    <textarea name="mensaje" class="form-input" rows="3" placeholder="Añadir notas o consulta..."></textarea>
                </div>
                <div class="form-check lv-info-box" style="margin-bottom: 1rem;">
                    <input class="form-check-input" type="checkbox" name="newsletter" id="add-lead-newsletter" style="cursor: pointer;">
                    <label class="form-check-label" for="add-lead-newsletter" style="font-weight: 600; cursor: pointer; color: inherit; margin: 0;">
                        El contacto consiente envíos comerciales (Newsletter)
                    </label>
                </div>
                <div class="leads-form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar Contacto</button>
                </div>
            </form>
            </div>
        `;

        Utils.openModal('Añadir Contacto Manual', content);
    },

    async handleAddLead(event) {
        event.preventDefault();
        if (!this.canManage() || this._saving) return;
        const form = event.target;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }
        const formData = new FormData(form);
        const email = formData.get('email').trim().toLowerCase();
        const yearStart = parseInt(formData.get('academicYearStart'), 10);

        const data = {
            nombre: formData.get('nombre'),
            email: email,
            telefono: formData.get('telefono'),
            // A contact added by hand has no web text: its text is the plan's name.
            interes: this.planName(formData.get('studyPlanId')) || '',
            studyPlanId: formData.get('studyPlanId') || null,
            academicYearStart: !isNaN(yearStart) ? yearStart : this.todayTargetYearStart(),
            origen: formData.get('origen') || 'manual',
            lugar: formData.get('lugar'),
            mensaje: formData.get('mensaje'),
            newsletter: document.getElementById('add-lead-newsletter') ? document.getElementById('add-lead-newsletter').checked : false
        };

        await this.saveContact(form, () => LeadService.createLead(data), 'Contacto creado correctamente');
    },

    openEditModal(id) {
        if (!this.canManage()) return;
        const lead = LeadService.getLead(id);
        if (!lead) return;

        const formations = FormationService.getFormations();
        const currentLeadYear = this.getLeadTargetYearStart(lead);
        const currentYear = this.todayTargetYearStart();
        const editYears = new Set([currentYear, currentYear + 1, currentLeadYear, ...this.yearsList()]);
        const availableYears = [...editYears].sort((a, b) => b - a);

        const content = `
            <div class="dnr-proposal leads-form-dnr">
            <form id="edit-lead-form" ${UIEvents.attr("submit", function(event,captured){LeadsView.handleUpdateLead(event,String(captured[0]));}, [lead.id])}>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Nombre y Apellidos *</label>
                        <input type="text" name="nombre" class="form-input" value="${Utils.sanitizeHTML(lead.nombre)}" required>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Email *</label>
                        <input type="email" name="email" class="form-input" value="${Utils.sanitizeHTML(lead.email)}" required>
                    </div>
                </div>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Teléfono</label>
                        <input type="tel" name="telefono" class="form-input" value="${Utils.sanitizeHTML(lead.telefono || '')}">
                    </div>
                    <div class="form-group">
                        <label class="form-label">Plan de estudios de interés</label>
                        <select name="studyPlanId" class="form-input" style="cursor: pointer;">
                            <option value="" ${lead.studyPlanAssignedBy !== 'manual' ? 'selected' : ''}>Automático (según Configuración)${lead.studyPlanAssignedBy !== 'manual' && this.planName(lead.studyPlanId) ? ': ' + Utils.sanitizeHTML(this.planName(lead.studyPlanId)) : ''}</option>
                            ${this.planOptions(lead.studyPlanAssignedBy === 'manual' ? lead.studyPlanId : '')}
                        </select>
                        ${lead.interes ? `<small style="display: block; margin-top: 4px; color: var(--dnr-ink-mute);">Texto recibido: ${Utils.sanitizeHTML(lead.interes)}</small>` : ''}
                    </div>
                </div>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Curso Lectivo de Destino *</label>
                        <select name="academicYearStart" class="form-input" style="cursor: pointer;" required>
                            ${availableYears.map(y => `<option value="${y}" ${y === currentLeadYear ? 'selected' : ''}>Curso ${y} / ${y + 1}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label">Origen</label>
                        <input type="text" name="origen" class="form-input" value="${Utils.sanitizeHTML(lead.origen || '')}">
                    </div>
                </div>
                <div class="leads-form-row-2">
                    <div class="form-group">
                        <label class="form-label">Lugar/Sede</label>
                        <input type="text" name="lugar" class="form-input" value="${Utils.sanitizeHTML(lead.lugar || '')}">
                    </div>
                    <div class="form-group">
                        <label class="form-label">Estado de Gestión *</label>
                        <select name="status" class="form-input" style="cursor: pointer;" required>
                            <option value="pending" ${lead.status === 'pending' ? 'selected' : ''}>Pendiente</option>
                            <option value="contacted" ${lead.status === 'contacted' ? 'selected' : ''}>Contactado</option>
                            <option value="enrolled" ${lead.status === 'enrolled' ? 'selected' : ''}>Matriculado</option>
                            <option value="rejected" ${lead.status === 'rejected' ? 'selected' : ''}>Descartado</option>
                            <option value="visit" ${lead.status === 'visit' ? 'selected' : ''}>Visita (sin newsletter, pidió visitar)</option>
                            <option value="no_consent" ${lead.status === 'no_consent' ? 'selected' : ''}>Sin newsletter (no contactar)</option>
                        </select>
                    </div>
                </div>
                <div class="form-group">
                    <label class="form-label">Mensaje o Notas</label>
                    <textarea name="mensaje" class="form-input" rows="3">${Utils.sanitizeHTML(lead.mensaje || '')}</textarea>
                </div>
                <div class="form-check lv-info-box" style="margin-bottom: 1rem;">
                    <input class="form-check-input" type="checkbox" name="newsletter" id="edit-lead-newsletter" ${lead.newsletter ? 'checked' : ''} style="cursor: pointer;">
                    <label class="form-check-label" for="edit-lead-newsletter" style="font-weight: 600; cursor: pointer; color: inherit; margin: 0;">
                        El contacto consiente envíos comerciales (Newsletter)
                    </label>
                </div>
                <div class="leads-form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar Cambios</button>
                </div>
            </form>
            </div>
        `;

        Utils.openModal('Editar Contacto', content);
    },

    async handleUpdateLead(event, id) {
        event.preventDefault();
        if (!this.canManage() || this._saving) return;
        const form = event.target;
        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }
        const formData = new FormData(form);
        const email = formData.get('email').trim().toLowerCase();
        const yearStart = parseInt(formData.get('academicYearStart'), 10);

        const data = {
            nombre: formData.get('nombre'),
            email: email,
            telefono: formData.get('telefono'),
            // The text received is kept; only a contact without one takes the formation's name.
            ...(LeadService.getLead(id)?.interes ? {} : {interes: this.planName(formData.get('studyPlanId')) || ''}),
            studyPlanId: formData.get('studyPlanId') || null,
            academicYearStart: !isNaN(yearStart) ? yearStart : this.todayTargetYearStart(),
            origen: formData.get('origen'),
            lugar: formData.get('lugar'),
            mensaje: formData.get('mensaje'),
            status: formData.get('status'),
            newsletter: document.getElementById('edit-lead-newsletter') ? document.getElementById('edit-lead-newsletter').checked : false
        };

        await this.saveContact(form, () => LeadService.updateLead(id, data), 'Contacto actualizado correctamente');
    },

    async saveContact(form, save, message) {
        if (this._saving) return;
        this._saving = true;
        const epoch = AuthManager._epoch;
        const button = form?.querySelector('[type="submit"]');
        if (button) button.disabled = true;
        try {
            await save();
            if (epoch !== AuthManager._epoch || !form.isConnected) return;
            Utils.showToast(message, 'success');
            Utils.closeModal();
            if (App.currentView === 'leads') {
                SafeHTML.set(document.getElementById('content-area'), this.render());
                this.init();
            }
        } catch (error) {
            if (epoch === AuthManager._epoch && form.isConnected) Utils.showToast(error.message || 'No se pudo guardar el contacto.', 'error');
        } finally {
            this._saving = false;
            if (button) button.disabled = false;
        }
    },

    async deleteLead(id) {
        if (!AuthManager.isAdmin() || this._saving) return;
        const result = await SafeHTML.fire({
            title: '¿Eliminar contacto?',
            text: '¿Estás seguro de que quieres eliminar este contacto? Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
            focusCancel: true
        });

        if (result.isConfirmed) {
            if (!AuthManager.isAdmin() || this._saving) return;
            this._saving = true;
            const epoch = AuthManager._epoch;
            try {
                await LeadService.deleteLead(id);
                if (epoch !== AuthManager._epoch) return;
                Utils.showToast('Contacto eliminado correctamente', 'success');
                if (App.currentView === 'leads') {
                    SafeHTML.set(document.getElementById('content-area'), this.render());
                    this.init();
                }
            } catch (error) {
                if (epoch === AuthManager._epoch) Utils.showToast(error.message || 'No se pudo eliminar el contacto.', 'error');
            } finally { this._saving = false; }
        }
    },

    // --- Bulk Email Modal (with dual-mode WYSIWYG / HTML Editor + saveable templates) ---
    // Visit request in the web form: any answer but "no"/empty (same rule as the server).
    wantsVisit(lead) {
        const answer = String(lead?.visita || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
        return !!answer && !['no', 'false'].includes(answer);
    },

    // One-off invitation to visit: allowed without newsletter (it answers the contact's own
    // request), never repeated, never for enrolled contacts. Campaigns still need newsletter.
    canInviteToVisit(lead) {
        return AuthManager.isAdmin() && !!lead && this.wantsVisit(lead) && !lead.visitInvitedAt && lead.status !== 'enrolled' && !this.isStudent(lead);
    },

    // Progress of the campaign being sent (or the last one, for a week), refreshed every 30 s while
    // something is being sent and this view is open.
    async refreshCampaigns() {
        clearTimeout(this._campaignTimer);
        const box = document.getElementById('lv-campaign-status');
        if (!box || !AuthManager.isAdmin()) return;
        const campaigns = await LeadService.getCampaigns().catch(() => []);
        if (!document.body.contains(box)) return;
        const current = campaigns.find(c => c.status === 'sending');
        const last = campaigns.find(c => c.status !== 'sending' && c.finishedAt && Date.now() - new Date(c.finishedAt).getTime() < 7 * 86400000);
        const c = current || last;
        if (!c) { SafeHTML.set(box, ''); return; }
        const done = c.sent + c.failed + c.skipped, pct = c.total ? Math.round(100 * done / c.total) : 100;
        const extra = [c.failed ? `${c.failed} fallido${c.failed === 1 ? '' : 's'}` : '', c.skipped ? `${c.skipped} omitido${c.skipped === 1 ? '' : 's'} (bajas o matriculados)` : ''].filter(Boolean).join(' · ');
        SafeHTML.set(box, current ? `
            <div class="lv-campaign is-sending">
                <div class="lv-campaign-text"><i class="fas fa-paper-plane"></i> Enviando «${Utils.sanitizeHTML(c.subject)}»: <strong>${c.sent} de ${c.total}</strong> enviados${extra ? ' · ' + extra : ''} · quedan unos ${Math.max(1, c.pending)} min</div>
                <div class="lv-campaign-bar"><span style="width: ${pct}%;"></span></div>
                <button type="button" class="lv-btn is-small" ${UIEvents.attr("click", function(event,captured){LeadsView.cancelCampaign(String(captured[0]));}, [c.id])}>Detener envío</button>
            </div>` : `
            <div class="lv-campaign">
                <div class="lv-campaign-text"><i class="fas fa-check-circle"></i> ${c.status === 'cancelled' ? 'Envío detenido' : 'Campaña enviada'}: «${Utils.sanitizeHTML(c.subject)}» · <strong>${c.sent} correos enviados de ${c.total}</strong>${extra ? ' · ' + extra : ''}</div>
            </div>`);
        if (current) this._campaignTimer = setTimeout(() => this.refreshCampaigns(), 30000);
    },

    async cancelCampaign(id) {
        const answer = await SafeHTML.fire({ title: 'Detener el envío', text: 'Los correos que aún no han salido no se enviarán. Los ya enviados no se pueden recuperar.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Detener', cancelButtonText: 'Seguir enviando' });
        if (!answer.isConfirmed) return;
        try { await LeadService.cancelCampaign(id); Utils.showToast('Envío detenido.', 'success'); }
        catch (error) { Utils.showToast(error.message, 'warning'); }
        this.refreshCampaigns();
    },

    // Campaign dates (kept while the modal is reopened with another template): since 1 September.
    campaignRange() {
        if (!this._campaignRange) {
            const now = new Date(), year = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
            this._campaignRange = { from: `${year}-09-01`, to: this.localDay(now) };
        }
        return this._campaignRange;
    },
    localDay(date) {
        const d = new Date(date);
        if (Number.isNaN(d.getTime())) return '';
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    rangeCountHtml(count, omitted) {
        return `<strong>${count}</strong> contacto${count === 1 ? '' : 's'} con newsletter recibirá${count === 1 ? '' : 'n'} el correo` +
            (omitted ? ` <span style="color: var(--text-secondary);">· se omiten ${omitted} (sin newsletter o ya matriculados)</span>` : '');
    },
    async updateCampaignRange(field, value) {
        this.campaignRange()[field] = value;
        const { from, to } = this.campaignRange();
        // Contacts of those dates come from the server: the browser holds one year only.
        const inRange = await LeadService.fetchRange(from, to).catch(() => []);
        if (this.campaignRange().from !== from || this.campaignRange().to !== to) return;
        this._rangeRecipients = inRange.filter(lead => lead.newsletter && lead.status !== 'enrolled' && !this.isStudent(lead));
        const box = document.getElementById('campaign-range-count');
        if (box) SafeHTML.set(box, this.rangeCountHtml(this._rangeRecipients.length, inRange.length - this._rangeRecipients.length));
    },

    // Header of the default templates: the e-mail logo from Ajustes (it must be https to be shown
    // in e-mails), or the school's names as text.
    emailHeader() {
        const short = Utils.sanitizeHTML(SchoolService.shortName()), name = Utils.sanitizeHTML(SchoolService.name());
        let src = '';
        try { src = SchoolService.get('emailLogoUrl') ? new URL(SchoolService.get('emailLogoUrl'), location.origin).href : ''; } catch { src = ''; }
        if (src.startsWith('https://')) return `<img src="${Utils.sanitizeHTML(src)}" alt="${short}" style="max-width: 200px; max-height: 70px;">`;
        return `<span style="font-size: 22px; font-weight: 800; color: #0c4a60; letter-spacing: 0.05em; text-transform: uppercase;">${short}</span>
                                <div style="font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase; margin-top: 3px; letter-spacing: 0.08em;">${name}</div>`;
    },

    // Default one-off visit invitation (also shown in Plantillas de correo).
    visitInvitationDefault() {
        return { subject: `Tu visita a ${SchoolService.shortName()}`, body: `
                        <div style="background-color: #f8fafc; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 30px 20px; color: #334155; line-height: 1.6; font-size: 16px; width: 100%; max-width: 600px; margin: 0 auto; border-radius: 12px; box-sizing: border-box;">
                            <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px;">
                                ${this.emailHeader()}
                            </div>
                            <div style="background-color: #ffffff; padding: 25px; border-radius: 8px; border: 1px solid #e2e8f0;">
                                <p style="margin-top: 0; font-size: 17px; font-weight: 700; color: #0c4a60;">Hola, {{nombrePila}}:</p>
                                <p style="margin-bottom: 15px;">Gracias por tu interés. Nos pediste visitar la escuela y nos encantará enseñártela: las aulas, los platós y el material con el que trabajan nuestros alumnos.</p>
                                <p style="margin-bottom: 15px;">Responde a este correo con el día y la franja horaria que mejor te vengan y te confirmamos la cita.</p>
                                <div style="border-top: 1px solid #f1f5f9; padding-top: 15px; margin-top: 25px; font-size: 13px; color: #64748b;">
                                    Un saludo,<br>
                                    <strong style="color: #0c4a60; font-weight: 700;">Equipo de Admisiones</strong><br>
                                    <span style="font-size: 11px; color: #94a3b8;">${Utils.sanitizeHTML(SchoolService.name())}</span>
                                </div>
                            </div>
                            <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #94a3b8; line-height: 1.4;">
                                Recibes este único correo porque solicitaste visitar la escuela. No te enviaremos publicidad.
                            </div>
                        </div>
            ` };
    },

    async openBulkEmailModal(singleLeadId = null, templateType = 'admissions') {
        if (!AuthManager.isAdmin()) { Utils.showToast('El editor de correos está reservado a administradores.', 'warning'); return; }
        if (!['admissions', 'puertas_abiertas', 'ultimas_plazas', 'invitacion_visita'].includes(templateType)) return;
        const isVisitInvitation = templateType === 'invitacion_visita';
        if (isVisitInvitation && !this.canInviteToVisit(LeadService.getLead(singleLeadId))) {
            Utils.showToast('Este contacto no pidió visitar la escuela o ya recibió la invitación.', 'warning');
            return;
        }
        let allLeads = LeadService.getLeads();
        let leadIds, rangeMode = false;
        
        if (singleLeadId) {
            leadIds = [singleLeadId];
        } else if (this.selectedLeads.size > 0) {
            leadIds = Array.from(this.selectedLeads);
        } else {
            // Campaign by dates: the contacts who wrote between «Desde» and «Hasta» (default: since 1
            // September), asked to the server because they may belong to another year than the one shown.
            rangeMode = true;
            const { from, to } = this.campaignRange();
            try { allLeads = await LeadService.fetchRange(from, to); }
            catch (e) { Utils.showToast(e.message, 'error'); return; }
            leadIds = allLeads.map(l => l.id);
        }
        
        // Para correos individuales directos, no exigimos estrictamente la newsletter por ser comunicación transaccional/directa
        const selectedSubscribedLeads = singleLeadId 
            ? allLeads.filter(l => l.id === singleLeadId)
            : allLeads.filter(l => leadIds.includes(l.id) && l.newsletter && l.status !== 'enrolled' && !this.isStudent(l));

        if (rangeMode) this._rangeRecipients = selectedSubscribedLeads;
        if (selectedSubscribedLeads.length === 0 && !rangeMode) {
            Utils.showToast(singleLeadId ? 'El contacto seleccionado no se encuentra o ya está matriculado.' : 'No hay contactos en esta vista que tengan activada la newsletter y no estén matriculados.', 'warning');
            return;
        }

        const excludedCount = singleLeadId ? 0 : leadIds.length - selectedSubscribedLeads.length;
        this.editorMode = 'wysiwyg'; // reset mode
        this._currentTemplateType = templateType; // track for save button

        // Intentar cargar plantilla guardada del servidor
        const savedTemplate = await LeadService.getEmailTemplate(templateType);

        // Configurar asunto y cuerpo según la plantilla elegida
        const courseYearLabel = (this.currentYearFilter && this.currentYearFilter !== 'all')
            ? `${this.currentYearFilter}/${parseInt(this.currentYearFilter, 10) + 1}`
            : `${this.todayTargetYearStart()}/${this.todayTargetYearStart() + 1}`;
        let defaultSubject = `¡Matrícula Abierta en ${SchoolService.shortName()}! Curso ${courseYearLabel}`;
        let defaultBody = '';

        if (isVisitInvitation) {
            ({ subject: defaultSubject, body: defaultBody } = this.visitInvitationDefault());
        } else if (templateType === 'puertas_abiertas') {
            defaultSubject = `Invitación: Jornada de Puertas Abiertas en ${SchoolService.shortName()}`;
            defaultBody = `
                        <div style="background-color: #f8fafc; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 30px 20px; color: #334155; line-height: 1.6; font-size: 16px; width: 100%; max-width: 600px; margin: 0 auto; border-radius: 12px; box-sizing: border-box;">
                            <!-- Header Logo -->
                            <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px;">
                                ${this.emailHeader()}
                            </div>
                            
                            <!-- Card Body -->
                            <div style="background-color: #ffffff; padding: 25px; border-radius: 8px; border: 1px solid #e2e8f0;">
                                <p style="margin-top: 0; font-size: 17px; font-weight: 700; color: #0c4a60;">Hola, {{nombrePila}}:</p>
                                
                                <p style="margin-bottom: 15px;">Nos complace invitarte a nuestra próxima <strong>Jornada de Puertas Abiertas</strong>: una oportunidad para conocer ${Utils.sanitizeHTML(SchoolService.shortName())} por dentro.</p>
                                
                                <!-- When and where: fill in before sending -->
                                <table role="presentation" style="width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px solid #fde68a; border-radius: 8px;">
                                    <tr><td style="padding: 10px 14px; font-weight: 700; color: #78350f; width: 90px; border-bottom: 1px solid #fef3c7;">Fecha</td><td style="padding: 10px 14px; border-bottom: 1px solid #fef3c7;">[Escribe aquí la fecha]</td></tr>
                                    <tr><td style="padding: 10px 14px; font-weight: 700; color: #78350f; border-bottom: 1px solid #fef3c7;">Hora</td><td style="padding: 10px 14px; border-bottom: 1px solid #fef3c7;">[Escribe aquí la hora]</td></tr>
                                    <tr><td style="padding: 10px 14px; font-weight: 700; color: #78350f;">Lugar</td><td style="padding: 10px 14px;">${Utils.sanitizeHTML(SchoolService.get('address') || '[Escribe aquí el lugar]')}</td></tr>
                                </table>
                                
                                <!-- Callout Box -->
                                <div style="background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-left: 4px solid #d97706; padding: 18px; border-radius: 0 6px 6px 0; margin: 20px 0; text-align: left;">
                                    <div style="font-size: 12px; font-weight: 700; color: #b45309; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px;">Formación de interés</div>
                                    <div style="font-size: 18px; font-weight: 800; color: #78350f; line-height: 1.3;">{{interes}}</div>
                                    <p style="margin: 8px 0 0 0; font-size: 14px; color: #92400e;">Es la ocasión perfecta para que puedas visitar nuestras instalaciones, ver los platós de rodaje y aulas tecnológicas, y conversar de tú a tú con coordinadores y profesores de tu especialidad.</p>
                                </div>

                                <p style="margin-bottom: 20px;">Las plazas son limitadas por aforo. Si deseas asistir y venir acompañado, te sugerimos reservar tu acreditación gratuita lo antes posible.</p>
                                
                                <!-- CTA Button -->
                                <div style="text-align: center; margin: 25px 0;">
                                    <a href="${Utils.sanitizeHTML(SchoolService.get('website') || '#')}" style="background-color: #d97706; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 50px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 8px rgba(217,119,6,0.2);">Confirmar Asistencia Gratuita</a>
                                </div>
                                
                                <!-- Signature -->
                                <div style="border-top: 1px solid #f1f5f9; padding-top: 15px; margin-top: 25px; font-size: 13px; color: #64748b;">
                                    Te esperamos,<br>
                                    <strong style="color: #0c4a60; font-weight: 700;">Equipo de Coordinación y Eventos</strong><br>
                                    <span style="font-size: 11px; color: #94a3b8;">${Utils.sanitizeHTML(SchoolService.name())} | ${Utils.sanitizeHTML(SchoolService.shortName())}</span>
                                </div>
                            </div>
                            
                            <!-- Footer -->
                            <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #94a3b8; line-height: 1.4;">
                                Recibes esta invitación como parte del seguimiento de tu solicitud de información.<br>
                                © ${new Date().getFullYear()} ${Utils.sanitizeHTML(SchoolService.name())}. Todos los derechos reservados.
                            </div>
                        </div>
            `;
        } else if (templateType === 'ultimas_plazas') {
            defaultSubject = `¡Últimas Plazas Disponibles! - ${SchoolService.shortName()}`;
            defaultBody = `
                        <div style="background-color: #f8fafc; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 30px 20px; color: #334155; line-height: 1.6; font-size: 16px; width: 100%; max-width: 600px; margin: 0 auto; border-radius: 12px; box-sizing: border-box;">
                            <!-- Header Logo -->
                            <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px;">
                                ${this.emailHeader()}
                            </div>
                            
                            <!-- Card Body -->
                            <div style="background-color: #ffffff; padding: 25px; border-radius: 8px; border: 1px solid #e2e8f0;">
                                <p style="margin-top: 0; font-size: 17px; font-weight: 700; color: #b91c1c;">Hola, {{nombrePila}}:</p>
                                
                                <p style="margin-bottom: 15px;">Te escribimos con carácter preferente para informarte de que el grupo para la formación <strong>{{interes}}</strong> está muy cerca de completarse.</p>
                                
                                <!-- Callout Box -->
                                <div style="background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); border-left: 4px solid #ef4444; padding: 18px; border-radius: 0 6px 6px 0; margin: 20px 0; text-align: left;">
                                    <div style="font-size: 12px; font-weight: 700; color: #b91c1c; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px;">Convocatoria de Matrícula</div>
                                    <div style="font-size: 18px; font-weight: 800; color: #7f1d1d; line-height: 1.3;">{{interes}}</div>
                                    <p style="margin: 8px 0 0 0; font-size: 14px; color: #991b1b;">Con el fin de garantizar una formación práctica y personalizada de máxima calidad en plató, el cupo de alumnos por aula es rigurosamente limitado. Quedan muy pocas plazas libres para el inicio del curso.</p>
                                </div>

                                <p style="margin-bottom: 20px;">Si estás interesado en asegurar tu plaza para esta convocatoria, te recomendamos formalizar el proceso de matrícula a la mayor brevedad.</p>
                                
                                <!-- CTA Button -->
                                <div style="text-align: center; margin: 25px 0;">
                                    <a href="${Utils.sanitizeHTML(SchoolService.get('website') || '#')}" style="background-color: #ef4444; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 50px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 8px rgba(239,68,68,0.2);">Reservar mi Plaza Ahora</a>
                                </div>
                                
                                <!-- Signature -->
                                <div style="border-top: 1px solid #f1f5f9; padding-top: 15px; margin-top: 25px; font-size: 13px; color: #64748b;">
                                    Un saludo cordial,<br>
                                    <strong style="color: #0c4a60; font-weight: 700;">Departamento de Admisiones y Reservas</strong><br>
                                    <span style="font-size: 11px; color: #94a3b8;">${Utils.sanitizeHTML(SchoolService.name())} | ${Utils.sanitizeHTML(SchoolService.shortName())}</span>
                                </div>
                            </div>
                            
                            <!-- Footer -->
                            <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #94a3b8; line-height: 1.4;">
                                Recibes esta notificación de prioridad debido a tu solicitud de información previa.<br>
                                © ${new Date().getFullYear()} ${Utils.sanitizeHTML(SchoolService.name())}. Todos los derechos reservados.
                            </div>
                        </div>
            `;
        } else {
            defaultBody = `
                        <div style="background-color: #f8fafc; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 30px 20px; color: #334155; line-height: 1.6; font-size: 16px; width: 100%; max-width: 600px; margin: 0 auto; border-radius: 12px; box-sizing: border-box;">
                            <!-- Header Logo -->
                            <div style="text-align: center; margin-bottom: 25px; border-bottom: 2px solid #e2e8f0; padding-bottom: 15px;">
                                ${this.emailHeader()}
                            </div>
                            
                            <!-- Card Body -->
                            <div style="background-color: #ffffff; padding: 25px; border-radius: 8px; border: 1px solid #e2e8f0;">
                                <p style="margin-top: 0; font-size: 17px; font-weight: 700; color: #0c4a60;">Hola, {{nombrePila}}:</p>
                                
                                <p style="margin-bottom: 15px;">Te escribimos desde <strong>${Utils.sanitizeHTML(SchoolService.name())}</strong> para agradecer tu interés en nuestra oferta formativa.</p>
                                
                                <!-- Callout Box -->
                                <div style="background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%); border-left: 4px solid #0b5f59; padding: 18px; border-radius: 0 6px 6px 0; margin: 20px 0; text-align: left;">
                                    <div style="font-size: 12px; font-weight: 700; color: #0b5f59; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px;">Formación de interés</div>
                                    <div style="font-size: 18px; font-weight: 800; color: #0c4a60; line-height: 1.3;">{{interes}}</div>
                                    <p style="margin: 8px 0 0 0; font-size: 14px; color: #475569;">Queríamos informarte de que ya se encuentra abierto el plazo de inscripción para el próximo curso académico. Recuerda que las plazas se asignan por orden de matrícula.</p>
                                </div>

                                <p style="margin-bottom: 20px;">Si deseas concertar una entrevista personalizada, programar una visita a nuestras instalaciones o resolver dudas sobre el plan de estudios, estamos a tu entera disposición.</p>
                                
                                <!-- CTA Button -->
                                <div style="text-align: center; margin: 25px 0;">
                                    <a href="${Utils.sanitizeHTML(SchoolService.get('website') || '#')}" style="background-color: #0b5f59; color: #ffffff; text-decoration: none; padding: 12px 24px; border-radius: 50px; font-weight: 700; font-size: 14px; display: inline-block; box-shadow: 0 4px 8px rgba(11,95,89,0.2);">Conoce la Escuela y Matricúlate</a>
                                </div>
                                
                                <!-- Signature -->
                                <div style="border-top: 1px solid #f1f5f9; padding-top: 15px; margin-top: 25px; font-size: 13px; color: #64748b;">
                                    Atentamente,<br>
                                    <strong style="color: #0c4a60; font-weight: 700;">Equipo de Admisiones y Matrícula</strong><br>
                                    <span style="font-size: 11px; color: #94a3b8;">${Utils.sanitizeHTML(SchoolService.name())} | ${Utils.sanitizeHTML(SchoolService.shortName())}</span>
                                </div>
                            </div>
                            
                            <!-- Footer -->
                            <div style="text-align: center; margin-top: 20px; font-size: 10px; color: #94a3b8; line-height: 1.4;">
                                Recibes este correo en respuesta a tu consulta web.<br>
                                © ${new Date().getFullYear()} ${Utils.sanitizeHTML(SchoolService.name())}. Todos los derechos reservados.
                            </div>
                        </div>
            `;
        }

        // Si hay plantilla guardada, sobreescribir defaults
        const isCustom = !!(savedTemplate && savedTemplate.subject && savedTemplate.body);
        if (isCustom) {
            defaultSubject = savedTemplate.subject;
            defaultBody = savedTemplate.body;
        }
        // Guardar el body original hardcodeado para poder restaurar
        this._defaultBodies = this._defaultBodies || {};
        if (!isCustom) this._defaultBodies[templateType] = defaultBody;
        this._defaultSubjects = this._defaultSubjects || {};
        if (!isCustom) this._defaultSubjects[templateType] = defaultSubject;

        const content = `
            <div>
                <div style="margin-bottom: 1rem; font-size: 0.9rem; color: var(--text-secondary); background: var(--bg-secondary); padding: 0.75rem 1rem; border-radius: 8px; border: 1px solid var(--border-color);">
                    ${rangeMode ? `
                    <div style="display: flex; gap: 0.75rem; align-items: end; flex-wrap: wrap; margin-bottom: 0.6rem;">
                        <span style="font-weight: 700; color: var(--text-primary); align-self: center;"><i class="fas fa-calendar-alt" style="color: var(--primary-color);"></i> Contactos que escribieron</span>
                        <label style="display: flex; flex-direction: column; font-size: 0.75rem; font-weight: 700;">Desde
                            <input type="date" class="form-input" id="campaign-from" value="${this.campaignRange().from}" style="min-width: 150px;" ${UIEvents.attr("change", function(event,captured){LeadsView.updateCampaignRange('from', this.value);}, [])}></label>
                        <label style="display: flex; flex-direction: column; font-size: 0.75rem; font-weight: 700;">Hasta
                            <input type="date" class="form-input" id="campaign-to" value="${this.campaignRange().to}" style="min-width: 150px;" ${UIEvents.attr("change", function(event,captured){LeadsView.updateCampaignRange('to', this.value);}, [])}></label>
                    </div>
                    <div id="campaign-range-count">${this.rangeCountHtml(selectedSubscribedLeads.length, leadIds.length - selectedSubscribedLeads.length)}</div>` : `
                    <i class="fas fa-paper-plane" style="color: var(--primary-color);"></i> Destinatarios: 
                    <strong>${singleLeadId ? `${Utils.sanitizeHTML(selectedSubscribedLeads[0].nombre)} &lt;${Utils.sanitizeHTML(selectedSubscribedLeads[0].email)}&gt;` : `${selectedSubscribedLeads.length} contactos seleccionados`}</strong>
                    ${excludedCount > 0 ? `<span style="color: var(--danger-color); font-weight: 500;">(Se omitirán ${excludedCount} por falta de consentimiento o estar matriculados)</span>` : ''}`}
                </div>

                ${isVisitInvitation ? '' : `
                <div class="form-group" style="margin-bottom: 1rem;">
                    <label class="form-label" for="email-template-select" style="font-weight: 700;">Plantilla</label>
                    <select id="email-template-select" class="form-input" ${UIEvents.attr("change", function(event,captured){LeadsView.openBulkEmailModal(captured[0] || null, this.value);}, [singleLeadId || ''])}>
                        ${[['admissions', 'Matrícula abierta (general)'], ['puertas_abiertas', 'Jornada de puertas abiertas'], ['ultimas_plazas', 'Últimas plazas']]
                            .map(([value, label]) => `<option value="${value}" ${value === templateType ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                    <small class="text-muted" style="display: block; margin-top: 0.35rem;">Cambiar de plantilla descarta los cambios que no hayas guardado.</small>
                </div>`}

                <div class="form-group" style="margin-bottom: 1.25rem;">
                    <label class="form-label" style="font-weight: 700;">Asunto del Correo *</label>
                    <input type="text" id="email-subject-input" class="form-input" required placeholder="Asunto del correo" value="${Utils.sanitizeHTML(defaultSubject)}">
                </div>

                <!-- Rich Editor Toolbar -->
                <div style="display: flex; justify-content: space-between; align-items: center; background: #f8fafc; border: 1px solid var(--border-color); border-bottom: none; border-radius: 8px 8px 0 0; padding: 6px 12px; gap: 10px; flex-wrap: wrap;">
                    <!-- Formatting Buttons -->
                    <div id="editor-toolbar-actions" style="display: flex; gap: 4px; align-items: center; flex-wrap: wrap;">
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.execEditorCommand('bold');}, [])} title="Negrita" style="padding: 4px 8px;"><i class="fas fa-bold"></i></button>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.execEditorCommand('italic');}, [])} title="Cursiva" style="padding: 4px 8px;"><i class="fas fa-italic"></i></button>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.execEditorCommand('underline');}, [])} title="Subrayado" style="padding: 4px 8px;"><i class="fas fa-underline"></i></button>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.execEditorCommand('createLink');}, [])} title="Enlace" style="padding: 4px 8px;"><i class="fas fa-link"></i></button>
                        <div style="width: 1px; height: 18px; background: #cbd5e1; margin: 0 4px;"></div>
                        <!-- Placeholders -->
                        <span style="font-size: 0.75rem; font-weight: 700; color: var(--text-secondary); margin-right: 2px;">Campos:</span>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.insertPlaceholder('{{nombrePila}}');}, [])} title="Solo el nombre, con mayúscula: «David»" style="font-size: 0.75rem; font-weight: 700; padding: 2px 6px;">{{nombrePila}}</button>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.insertPlaceholder('{{nombre}}');}, [])} title="El nombre completo tal como lo escribió" style="font-size: 0.75rem; font-weight: 700; padding: 2px 6px;">{{nombre}}</button>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.insertPlaceholder('{{interes}}');}, [])} style="font-size: 0.75rem; font-weight: 700; padding: 2px 6px;">{{interes}}</button>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.insertPlaceholder('{{lugar}}');}, [])} style="font-size: 0.75rem; font-weight: 700; padding: 2px 6px;">{{lugar}}</button>
                    </div>
                    
                    <!-- Right side: Toggle + Save template buttons -->
                    <div style="display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
                        ${isCustom ? `<button type="button" class="btn btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.restoreTemplate(String(captured[0]));}, [templateType])} style="font-size: 0.75rem; padding: 4px 10px; border-radius: 4px; background: #fef3c7; color: #92400e; border: 1px solid #fbbf24; font-weight: 600;" title="Volver a la plantilla original">
                            <i class="fas fa-undo"></i> Restaurar original
                        </button>` : ''}
                        <button type="button" id="save-template-btn" class="btn btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.saveTemplate(String(captured[0]));}, [templateType])} style="font-size: 0.75rem; padding: 4px 10px; border-radius: 4px; background: #f0fdf4; color: #166534; border: 1px solid #86efac; font-weight: 600;" title="Guardar como plantilla por defecto">
                            <i class="fas fa-save"></i> Guardar plantilla
                        </button>
                        <button type="button" id="toggle-editor-mode-btn" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){LeadsView.toggleEditorMode();}, [])} style="font-weight: 600; font-size: 0.75rem; padding: 4px 10px; border-radius: 4px; display: flex; align-items: center; gap: 4px;">
                            <i class="fas fa-code"></i> Editar HTML
                        </button>
                    </div>
                </div>

                <!-- Editor Containers -->
                <div style="position: relative;">
                    <!-- WYSIWYG Editor Container -->
                    <div id="email-body-wysiwyg" contenteditable="true" style="min-height: 250px; max-height: 400px; overflow-y: auto; border: 1px solid var(--border-color); border-radius: 0 0 8px 8px; padding: 1.5rem; background: white; font-family: 'Source Sans 3', sans-serif; font-size: 1rem; line-height: 1.6; outline: none; box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);">
                        ${EmailHtml.clean(defaultBody)}
                    </div>
                    
                    <!-- HTML Source Code Area -->
                    <textarea id="email-body-html" class="form-input" style="min-height: 250px; max-height: 400px; display: none; border-radius: 0 0 8px 8px; font-family: 'Courier New', Courier, monospace; font-size: 0.9rem; line-height: 1.4; border: 1px solid var(--border-color); padding: 1rem; background: #0f172a; color: #cbd5e1; box-shadow: inset 0 2px 4px rgba(0,0,0,0.05); resize: vertical;"></textarea>
                </div>
                
                <div style="margin-top: 1rem; font-size: 0.8rem; color: var(--text-secondary); line-height: 1.4;">
                    <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){EmailHtml.previewEditor('email-body-wysiwyg','email-body-html',LeadsView.editorMode);}, [])}>Vista previa segura</button>
                    <i class="fas fa-magic" style="color: var(--primary-color);"></i> Puedes personalizar el mensaje usando <strong>{{nombrePila}}</strong> (solo el nombre), <strong>{{nombre}}</strong> (completo), <strong>{{interes}}</strong> y <strong>{{lugar}}</strong>. Se reemplazan para cada destinatario al enviar. Al pie se añade siempre el enlace «Darte de baja».
                </div>
            </div>
        `;

        ModalManager.show(isVisitInvitation ? 'Invitación a visitar la escuela (un solo envío)' : 'Redactar Correo Masivo', content, [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            },
            {
                text: isVisitInvitation ? 'Enviar invitación' : 'Enviar Correos',
                class: 'btn-primary',
                action: async () => {
                    const subject = document.getElementById('email-subject-input').value.trim();
                    if (!subject) {
                        Utils.showToast('El asunto del correo es obligatorio', 'danger');
                        return;
                    }

                    // Get content depending on current mode
                    let bodyTemplate = '';
                    if (this.editorMode === 'wysiwyg') {
                        bodyTemplate = document.getElementById('email-body-wysiwyg').innerHTML;
                    } else {
                        bodyTemplate = document.getElementById('email-body-html').value;
                    }

                    if (!bodyTemplate || bodyTemplate.replace(/<[^>]*>/g, '').trim() === '') {
                        Utils.showToast('El cuerpo del correo no puede estar vacío', 'danger');
                        return;
                    }

                    // Show sending state
                    const btnSend = document.querySelector('.modal-container .btn-primary');
                    const btnCancel = document.querySelector('.modal-container .btn-secondary');
                    if (btnSend) {
                        btnSend.disabled = true;
                        SafeHTML.set(btnSend, '<i class="fas fa-spinner fa-spin"></i> Enviando...');
                    }
                    if (btnCancel) btnCancel.disabled = true;

                    try {
                        if (isVisitInvitation) {
                            // One-off: the server records it and refuses a second one.
                            await LeadService.sendVisitInvitation(singleLeadId, subject, bodyTemplate);
                            Utils.showToast('Invitación a visitar enviada. No se podrá volver a enviar a este contacto.', 'success');
                            ModalManager.close();
                            SafeHTML.set(document.getElementById('content-area'), LeadsView.render());
                            LeadsView.init();
                            return;
                        }
                        const recipients = rangeMode ? (LeadsView._rangeRecipients || []) : selectedSubscribedLeads;
                        const restore = () => {
                            if (btnSend) { btnSend.disabled = false; btnSend.textContent = 'Enviar Correos'; }
                            if (btnCancel) btnCancel.disabled = false;
                        };
                        if (/\[Escribe aquí/.test(bodyTemplate)) { Utils.showToast('Rellena los datos entre corchetes (fecha, hora, lugar…) antes de enviar.', 'warning'); restore(); return; }
                        if (!recipients.length) { Utils.showToast('No hay contactos con newsletter en esas fechas.', 'warning'); restore(); return; }
                        const minutes = Math.max(1, recipients.length - 1);
                        const confirmed = await SafeHTML.fire({title: `Enviar ${recipients.length} correo${recipients.length === 1 ? '' : 's'}`,
                            text: `Se enviará uno por minuto (unos ${minutes >= 60 ? Math.floor(minutes / 60) + ' h ' + (minutes % 60) + ' min' : minutes + ' min'}). Puedes seguir usando SICAF y cerrar la ventana: verás el progreso en Contactos y te avisaremos al terminar.`,
                            icon: 'question', showCancelButton: true, confirmButtonText: 'Enviar', cancelButtonText: 'Volver'});
                        if (!confirmed.isConfirmed) { restore(); return; }
                        const result = await LeadService.sendBulkEmail(recipients.map(l => l.id), subject, bodyTemplate);
                        if (!result.success) throw new Error(result.error || 'Error desconocido');
                        Utils.showToast(`Campaña en marcha: ${result.total} correos, uno por minuto.`, 'success');
                        ModalManager.close();
                        SafeHTML.set(document.getElementById('content-area'), LeadsView.render());
                        LeadsView.init();
                    } catch (err) {
                        console.error('Error sending bulk emails:', err);
                        Utils.showToast(`Error al enviar: ${err.message}`, 'danger');
                        
                        // Restore buttons
                        if (btnSend) {
                            btnSend.disabled = false;
                            btnSend.textContent = isVisitInvitation ? 'Enviar invitación' : 'Enviar Correos';
                        }
                        if (btnCancel) btnCancel.disabled = false;
                    }
                }
            }
        ]);
        EmailHtml.mount(document.getElementById('email-body-wysiwyg'), document.getElementById('email-body-html'));
    },

    // --- Dual Editor Functions ---
    toggleEditorMode() {
        const wysiwygDiv = document.getElementById('email-body-wysiwyg');
        const htmlTextarea = document.getElementById('email-body-html');
        const modeBtn = document.getElementById('toggle-editor-mode-btn');
        const toolbarActions = document.getElementById('editor-toolbar-actions');

        if (!wysiwygDiv || !htmlTextarea || !modeBtn) return;

        if (this.editorMode === 'wysiwyg') {
            // Switch to HTML mode
            const htmlContent = wysiwygDiv.innerHTML;
            htmlTextarea.value = htmlContent;
            
            wysiwygDiv.style.display = 'none';
            htmlTextarea.style.display = 'block';
            
            SafeHTML.set(modeBtn, '<i class="fas fa-eye"></i> Editar Formato Visual');
            modeBtn.classList.add('btn-primary');
            modeBtn.classList.remove('btn-secondary');
            
            // Disable text formatting actions
            if (toolbarActions) {
                toolbarActions.querySelectorAll('button').forEach(btn => {
                    // Only disable formatting buttons, keep placeholders enabled
                    if (btn.querySelector('.fa-bold, .fa-italic, .fa-underline, .fa-link')) {
                        btn.style.opacity = '0.3';
                        btn.disabled = true;
                    }
                });
            }
            this.editorMode = 'html';
        } else {
            // Switch to WYSIWYG mode
            const htmlContent = htmlTextarea.value;
            try { EmailHtml.set(wysiwygDiv, htmlContent, htmlTextarea); }
            catch (error) { Utils.showToast(error.message, 'error'); return; }
            
            htmlTextarea.style.display = 'none';
            wysiwygDiv.style.display = 'block';
            
            SafeHTML.set(modeBtn, '<i class="fas fa-code"></i> Editar Código HTML');
            modeBtn.classList.remove('btn-primary');
            modeBtn.classList.add('btn-secondary');
            
            // Enable text formatting actions
            if (toolbarActions) {
                toolbarActions.querySelectorAll('button').forEach(btn => {
                    btn.style.opacity = '1';
                    btn.disabled = false;
                });
            }
            this.editorMode = 'wysiwyg';
        }
    },

    // --- Template Save / Restore ---
    async saveTemplate(templateType) {
        const subject = document.getElementById('email-subject-input')?.value?.trim();
        let body = '';
        if (this.editorMode === 'wysiwyg') {
            body = document.getElementById('email-body-wysiwyg')?.innerHTML || '';
        } else {
            body = document.getElementById('email-body-html')?.value || '';
        }

        if (!subject || !body.trim()) {
            Utils.showToast('El asunto y el cuerpo no pueden estar vacíos', 'danger');
            return;
        }

        const btn = document.getElementById('save-template-btn');
        if (btn) { btn.disabled = true; SafeHTML.set(btn, '<i class="fas fa-spinner fa-spin"></i> Guardando...'); }

        try {
            const saved = await LeadService.saveEmailTemplate(templateType, subject, body);
            const visual = document.getElementById('email-body-wysiwyg');
            const code = document.getElementById('email-body-html');
            if (visual && code && (this.editorMode === 'wysiwyg' ? visual.innerHTML : code.value) === body) EmailHtml.set(visual, saved.template.body, code);
            // Cache the saved values as new defaults for restore
            this._defaultBodies = this._defaultBodies || {};
            this._defaultSubjects = this._defaultSubjects || {};
            Utils.showToast('✅ Plantilla guardada correctamente. Se cargará por defecto la próxima vez.', 'success');
        } catch (err) {
            Utils.showToast('Error al guardar la plantilla: ' + err.message, 'danger');
        } finally {
            if (btn) { btn.disabled = false; SafeHTML.set(btn, '<i class="fas fa-save"></i> Guardar plantilla'); }
        }
    },

    restoreTemplate(templateType) {
        const originalBody = this._defaultBodies?.[templateType] || '';
        const originalSubject = this._defaultSubjects?.[templateType] || '';

        if (!originalBody) {
            Utils.showToast('No hay plantilla original almacenada en esta sesión. Abre el modal desde cero.', 'warning');
            return;
        }

        const subjectInput = document.getElementById('email-subject-input');
        const wysiwygDiv = document.getElementById('email-body-wysiwyg');
        const htmlTextarea = document.getElementById('email-body-html');

        if (subjectInput) subjectInput.value = originalSubject;
        if (wysiwygDiv) EmailHtml.set(wysiwygDiv, originalBody, htmlTextarea);

        Utils.showToast('Plantilla original restaurada en el editor. Guárdala si quieres conservarla.', 'success');
    },

    execEditorCommand(command) {
        if (this.editorMode !== 'wysiwyg') return;

        const wysiwygDiv = document.getElementById('email-body-wysiwyg');
        if (!wysiwygDiv) return;

        wysiwygDiv.focus();

        if (command === 'createLink') {
            const url = prompt('Introduce la URL del enlace:');
            if (url && EmailHtml.validLink(url)) {
                document.execCommand(command, false, url);
            }
        } else {
            document.execCommand(command, false, null);
        }
    },

    insertPlaceholder(placeholder) {
        if (this.editorMode === 'wysiwyg') {
            const wysiwygDiv = document.getElementById('email-body-wysiwyg');
            if (!wysiwygDiv) return;
            
            wysiwygDiv.focus();
            
            // Insert at caret position or append
            const selection = window.getSelection();
            if (selection.rangeCount > 0 && wysiwygDiv.contains(selection.anchorNode)) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode(placeholder);
                range.insertNode(textNode);
                // Move caret after inserted placeholder
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);
            } else {
                // Fallback: Append to body
                wysiwygDiv.appendChild(document.createTextNode(placeholder));
            }
        } else {
            const htmlTextarea = document.getElementById('email-body-html');
            if (!htmlTextarea) return;

            const startPos = htmlTextarea.selectionStart;
            const endPos = htmlTextarea.selectionEnd;
            const text = htmlTextarea.value;
            
            htmlTextarea.value = text.substring(0, startPos) + placeholder + text.substring(endPos, text.length);
            htmlTextarea.focus();
            htmlTextarea.selectionStart = startPos + placeholder.length;
            htmlTextarea.selectionEnd = startPos + placeholder.length;
        }
    }
};

window.LeadsView = LeadsView;
